import {Button} from '@wandb/components';
import {toast} from '@wandb/weave/common/components/elements/Toast';
import {isVersionAlias} from '@wandb/weave/common/util/artifacts';
import {TAG_COLOR_CONTRAST_WHEEL} from '@wandb/weave/components/Tag/utils';
import * as React from 'react';
import {useMemo, useState} from 'react';
import {Header, Modal} from 'semantic-ui-react';

import {TagAddButton} from './TagAddButton';
import {Tag, TagType} from './Tags';

export type AliasItem = {
  alias: string;
  isProtected?: boolean;
};

export const findCaseInsensitiveAliasCollision = (
  alias: string,
  existingAliases: Array<Pick<AliasItem, 'alias'>>
): string | undefined =>
  existingAliases.find(
    existingAlias =>
      existingAlias.alias.toLowerCase() === alias.toLowerCase() &&
      existingAlias.alias !== alias
  )?.alias;

interface AliasProps {
  isProtected?: boolean;
  alias: Pick<AliasItem, 'alias'>;
  onDelete?: () => Promise<any>;
  onClick?(): void;
  skipModal?: boolean;
  size?: 'large' | 'medium' | 'small';
}

export const Alias = (props: AliasProps) => {
  const {alias, onDelete, onClick, isProtected, skipModal, size} = props;
  const [isModalOpen, setModalOpen] = useState(false);
  const [isRemoving, setRemoving] = useState(false);

  const onTagDeleteAction = useMemo(() => {
    if (onDelete) {
      if (skipModal) {
        return onDelete;
      } else {
        return () => setModalOpen(true);
      }
    }
    return undefined;
  }, [onDelete, skipModal]);

  return (
    <>
      <Tag
        tag={{
          name: alias.alias,
          colorIndex: isProtected ? TagType.PROTECTED_ALIAS : TagType.ALIAS,
        }}
        noun={isProtected ? 'protected-alias' : 'alias'}
        size={size}
        onDelete={onTagDeleteAction}
        onClick={onClick}
      />
      <Modal open={isModalOpen} onClose={() => setModalOpen(false)}>
        <Header>
          <p>
            Are you sure you want to remove the <b>{alias.alias}</b> alias?
          </p>
        </Header>
        <Modal.Content>
          This will break any scripts that use this alias!
        </Modal.Content>
        <Modal.Actions>
          <Button variant="secondary" onPress={() => setModalOpen(false)}>
            Nevermind
          </Button>
          <Button
            ml={4}
            isPending={isRemoving}
            variant="destructive"
            onPress={async () => {
              setRemoving(true);

              if (onDelete) {
                await onDelete();
              }

              setRemoving(false);
              setModalOpen(false);
            }}>
            Remove alias
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

interface AddAliasButtonProps {
  aliases: Array<Pick<AliasItem, 'alias'>>;
  availableAliases: AliasItem[];
  onCreate: (newAlias: string) => Promise<any>;
}

export const AddAliasButton = (props: AddAliasButtonProps) => {
  const {aliases, availableAliases, onCreate} = props;

  return (
    <TagAddButton
      tags={aliases.map(a => {
        return {name: a.alias, colorIndex: TagType.ALIAS};
      })}
      availableTags={availableAliases.map(a => {
        // use TAG_COLOR_CONTRAST_WHEEL to map alias type to color index
        // directly instead of using the deprecated TagType enum
        const colorIndex = a.isProtected
          ? TAG_COLOR_CONTRAST_WHEEL.indexOf('moon')
          : TAG_COLOR_CONTRAST_WHEEL.indexOf('sienna');
        return {
          name: a.alias,
          colorIndex: colorIndex === -1 ? undefined : colorIndex,
        };
      })}
      addTag={async tag => {
        if (isVersionAlias({alias: tag.name})) {
          toast('Custom aliases matching /^v(d+)$/ are not allowed.');
          return;
        }

        const existingAlias = findCaseInsensitiveAliasCollision(
          tag.name,
          availableAliases
        );
        if (existingAlias != null) {
          toast(
            `The alias "${existingAlias}" already exists on this collection. Use that exact casing.`
          );
          return;
        }

        await onCreate(tag.name);
      }}
      compact={true}
      placement="bottom start"
      noun="alias"
    />
  );
};
